// URL route shape + path builders + pathname parsing.
//
// Single source of truth for what routes exist and how they map to the
// Shell's internal state (activeFilter / inboxView / selectedUrn /
// activePage / composeMode / selectedInviteId).
//
// ADR: docs/adr/0026-url-driven-routing.md
//
// Route table (see also `parseRoute` below):
//
//   /                                          → inbox (redirect)
//   /inbox                                     → activeFilter=INBOX, inboxView=ALL
//   /inbox/:view                               → view ∈ all | waiting-theirs |
//                                                waiting-mine | unread
//   /inbox/:view/c/:conversationUrn            → with conversation open
//   /:filter                                   → filter ∈ archive | starred |
//                                                drafts | reminders | inmail |
//                                                sponsored | spam | salesnav
//   /:filter/c/:conversationUrn                → with conversation open
//   /tag/:tagId                                → tag-filtered view
//   /tag/:tagId/c/:conversationUrn
//   /filter/:compositeId                       → saved filter (ADR 0029)
//   /filter/:compositeId/c/:conversationUrn
//   /pending                                   → PendingInvitations
//   /invites                                   → incoming invites
//   /invites/:invitationId                     → open invite
//   /connections                               → connections list
//   /compose                                   → new-message composer
//   /templates                                 → templates page
//   /settings                                  → settings (default section)
//   /settings/:section                         → section ∈ account |
//                                                preferences | crm | team |
//                                                billing
//
// Design notes:
//
//   - Conversation URN lives under `/c/` to avoid ambiguity with reserved
//     path segments (e.g. `:view` under `/inbox`). URN-looking strings
//     contain colons which url-encode; wrapping under `/c/` makes the
//     parse rule simple.
//   - Legacy internal `activeFilter` strings (INBOX, ARCHIVE, tag:<id>)
//     are still what Shell.tsx uses at its query/filter boundary — we
//     translate at the URL/state edge only. Prevents a mass-rewrite of
//     every downstream consumer.
//   - `inboxView` only makes sense under `/inbox`; other filters don't
//     have sub-views today. If they grow one, add nesting here.

import type { InboxView } from '../pages/Shell'

// ─── Filter slug ↔ internal key ─────────────────────────────────────────────
//
// Internal `activeFilter` uses uppercase keys (`INBOX`, `ARCHIVE`, ...)
// because that's how Supabase categories are stored. URL slugs are
// lowercase-kebab for readability. These two maps are the only place
// translation happens.

const FILTER_SLUG_TO_KEY: Record<string, string> = {
  inbox:     'INBOX',
  archive:   'ARCHIVE',
  starred:   'STARRED',
  drafts:    'DRAFTS',
  reminders: 'REMINDERS',
  inmail:    'INMAIL',
  sponsored: 'SPONSORED',
  spam:      'SPAM',
  salesnav:  'SALESNAV',
  recruiter: 'RECRUITER',
  pending:   'PENDING',
  invites:   'INVITES',
  connections: 'CONNECTIONS',
}

// Assert slug→key is injective before building the reverse map. Previously
// two slugs mapping to the same key (e.g. adding `archived → ARCHIVE` as an
// alias) would silently overwrite in the reverse map with no compile-time
// warning and a later `buildFilterRoute` call would pick whichever slug
// happened to land last. Dev-only assertion — thrown at module load so
// tests catch it before reaching any user.
{
  const keysSeen = new Set<string>()
  for (const key of Object.values(FILTER_SLUG_TO_KEY)) {
    if (keysSeen.has(key)) {
      throw new Error(
        `FILTER_SLUG_TO_KEY: duplicate internal key '${key}'. Reverse-map construction requires 1:1 mapping.`,
      )
    }
    keysSeen.add(key)
  }
}

const FILTER_KEY_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(FILTER_SLUG_TO_KEY).map(([slug, key]) => [key, slug]),
)

// ─── Inbox view slug ↔ enum ────────────────────────────────────────────────

const INBOX_VIEW_SLUG_TO_KEY: Record<string, InboxView> = {
  'all':            'ALL',
  'waiting-theirs': 'WAITING_THEIRS',
  'waiting-mine':   'WAITING_MINE',
  'unread':         'UNREAD',
}

const INBOX_VIEW_KEY_TO_SLUG: Record<InboxView, string> = {
  ALL:            'all',
  WAITING_THEIRS: 'waiting-theirs',
  WAITING_MINE:   'waiting-mine',
  UNREAD:         'unread',
}

// ─── Parsed shape ───────────────────────────────────────────────────────────

export interface ParsedRoute {
  /** Top-level page. Derived; not in the URL directly. */
  activePage: 'inbox' | 'templates' | 'settings' | 'compose'
  /** Internal filter key. Same vocabulary Shell.tsx uses today. */
  activeFilter: string
  /** Inbox sub-view mode. Only meaningful when activeFilter === 'INBOX'. */
  inboxView: InboxView
  /** Selected conversation URN, or null. */
  selectedUrn: string | null
  /** Selected invitation id when on /invites/:id. */
  selectedInviteId: string | null
  /** Settings section when activePage === 'settings'. */
  settingsSection: string | null
  /** Compose-mode overlay active. True iff path is /compose. */
  composeMode: boolean
}

const DEFAULT_ROUTE: ParsedRoute = {
  activePage: 'inbox',
  activeFilter: 'INBOX',
  inboxView: 'ALL',
  selectedUrn: null,
  selectedInviteId: null,
  settingsSection: null,
  composeMode: false,
}

// ─── Path builders ───────────────────────────────────────────────────────────
//
// Use these at every `navigate(...)` call site. Avoids hand-rolled string
// concatenation + gives a single place to change the URL shape.

/** Canonical encoder for URN-like segments. URNs contain colons that
 * aren't safe raw — let encodeURIComponent handle it. */
function enc(s: string): string {
  return encodeURIComponent(s)
}

export function buildInboxRoute(
  view: InboxView = 'ALL',
  conversationUrn?: string | null,
): string {
  const base = view === 'ALL'
    ? '/inbox'
    : `/inbox/${INBOX_VIEW_KEY_TO_SLUG[view]}`
  return conversationUrn
    ? `${base}/c/${enc(conversationUrn)}`
    : base
}

export function buildFilterRoute(
  filterKey: string,
  conversationUrn?: string | null,
): string {
  // Handle tag filters — internal key is `tag:<id>`
  if (filterKey.startsWith('tag:')) {
    const tagId = filterKey.slice(4)
    const base = `/tag/${enc(tagId)}`
    return conversationUrn ? `${base}/c/${enc(conversationUrn)}` : base
  }
  // Saved composite filters — internal key is `composite:<id>` (ADR 0029)
  if (filterKey.startsWith('composite:')) {
    const compositeId = filterKey.slice('composite:'.length)
    const base = `/filter/${enc(compositeId)}`
    return conversationUrn ? `${base}/c/${enc(conversationUrn)}` : base
  }
  // Sales Nav archive parallel view (kondo-parity 1.9) — internal key
  // `SALESNAV_ARCHIVE` → URL `/salesnav/archive`. Keep ordered above
  // the generic slug lookup since `SALESNAV_ARCHIVE` isn't in
  // FILTER_KEY_TO_SLUG.
  if (filterKey === 'SALESNAV_ARCHIVE') {
    const base = '/salesnav/archive'
    return conversationUrn ? `${base}/c/${enc(conversationUrn)}` : base
  }
  // Recruiter archive parallel view (kondo-parity 1.10).
  if (filterKey === 'RECRUITER_ARCHIVE') {
    const base = '/recruiter/archive'
    return conversationUrn ? `${base}/c/${enc(conversationUrn)}` : base
  }
  // INBOX uses the dedicated inbox route
  if (filterKey === 'INBOX') {
    return buildInboxRoute('ALL', conversationUrn)
  }
  const slug = FILTER_KEY_TO_SLUG[filterKey]
  if (!slug) return '/inbox' // unknown filter, fall back
  const base = `/${slug}`
  return conversationUrn ? `${base}/c/${enc(conversationUrn)}` : base
}

/** Path builder for saved composite filters. Thin wrapper over
 * `buildFilterRoute` for call sites that start from a composite id. */
export function buildCompositeRoute(
  compositeId: string,
  conversationUrn?: string | null,
): string {
  return buildFilterRoute(`composite:${compositeId}`, conversationUrn)
}

export const buildComposeRoute = (): string => '/compose'
export const buildTemplatesRoute = (): string => '/templates'
export const buildInvitesRoute = (invitationId?: string | null): string =>
  invitationId ? `/invites/${enc(invitationId)}` : '/invites'
export const buildConnectionsRoute = (): string => '/connections'
export const buildPendingRoute = (): string => '/pending'
export const buildSettingsRoute = (section?: string | null): string =>
  section ? `/settings/${section}` : '/settings'

// ─── Pathname → ParsedRoute ──────────────────────────────────────────────────
//
// Kept as a pure function so it's trivially testable without a real
// React Router. Shell.tsx calls this inside a `useMemo(..., [pathname])`
// to derive its state every render.

const CONV_SEGMENT = 'c'

const VALID_SETTINGS_SECTIONS = new Set([
  'account', 'preferences', 'crm', 'team', 'billing',
])

export function parseRoute(pathname: string): ParsedRoute {
  const segments = pathname.split('/').filter(Boolean)

  // Root or empty → default (inbox)
  if (segments.length === 0) return { ...DEFAULT_ROUTE }

  const first = segments[0]

  // Compose
  if (first === 'compose') {
    return { ...DEFAULT_ROUTE, activePage: 'compose', composeMode: true }
  }

  // Templates
  if (first === 'templates') {
    return { ...DEFAULT_ROUTE, activePage: 'templates', activeFilter: 'INBOX' }
  }

  // Settings / settings/:section
  if (first === 'settings') {
    const section = segments[1]
    const validSection = section && VALID_SETTINGS_SECTIONS.has(section) ? section : null
    return {
      ...DEFAULT_ROUTE,
      activePage: 'settings',
      settingsSection: validSection,
    }
  }

  // /invites or /invites/:id
  if (first === 'invites') {
    return {
      ...DEFAULT_ROUTE,
      activeFilter: 'INVITES',
      selectedInviteId: segments[1] ? decodeURIComponent(segments[1]) : null,
    }
  }

  // /tag/:tagId or /tag/:tagId/c/:conversationUrn
  if (first === 'tag' && segments[1]) {
    const tagId = decodeURIComponent(segments[1])
    const conv =
      segments[2] === CONV_SEGMENT && segments[3]
        ? decodeURIComponent(segments[3])
        : null
    return {
      ...DEFAULT_ROUTE,
      activeFilter: `tag:${tagId}`,
      selectedUrn: conv,
    }
  }

  // /filter/:compositeId or /filter/:compositeId/c/:conversationUrn
  // Saved Inbox Composite filters (ADR 0029). URL uses `/filter/` rather
  // than `/composite/` so the public URL reads as "a filter" to users;
  // internal `activeFilter` still uses the `composite:` prefix to keep
  // it distinct from tag filters at dispatch time.
  if (first === 'filter' && segments[1]) {
    const compositeId = decodeURIComponent(segments[1])
    const conv =
      segments[2] === CONV_SEGMENT && segments[3]
        ? decodeURIComponent(segments[3])
        : null
    return {
      ...DEFAULT_ROUTE,
      activeFilter: `composite:${compositeId}`,
      selectedUrn: conv,
    }
  }

  // /inbox or /inbox/:view or /inbox/:view/c/:conversationUrn
  if (first === 'inbox') {
    const viewSeg = segments[1]
    const inboxView =
      viewSeg && INBOX_VIEW_SLUG_TO_KEY[viewSeg]
        ? INBOX_VIEW_SLUG_TO_KEY[viewSeg]
        : 'ALL'

    // /inbox/c/:urn (no :view) or /inbox/:view/c/:urn
    let conv: string | null = null
    if (segments[1] === CONV_SEGMENT && segments[2]) {
      // /inbox/c/:urn
      conv = decodeURIComponent(segments[2])
    } else if (segments[2] === CONV_SEGMENT && segments[3]) {
      // /inbox/:view/c/:urn
      conv = decodeURIComponent(segments[3])
    }

    return {
      ...DEFAULT_ROUTE,
      activeFilter: 'INBOX',
      inboxView,
      selectedUrn: conv,
    }
  }

  // /salesnav/archive + /salesnav/archive/c/:urn — Sales Nav parallel
  // archive view (kondo-parity 1.9). Translated to the legacy-style
  // internal key `SALESNAV_ARCHIVE` so Shell's filter pass can
  // special-case the Sales Nav cursor without a broader rewrite.
  // Must come BEFORE the generic FILTER_SLUG_TO_KEY branch below so
  // `/salesnav/archive` doesn't get mis-matched as "salesnav with conv
  // at segment 1 = archive".
  if (first === 'salesnav' && segments[1] === 'archive') {
    const conv =
      segments[2] === CONV_SEGMENT && segments[3]
        ? decodeURIComponent(segments[3])
        : null
    return {
      ...DEFAULT_ROUTE,
      activeFilter: 'SALESNAV_ARCHIVE',
      selectedUrn: conv,
    }
  }

  // /recruiter/archive + /recruiter/archive/c/:urn — Recruiter parallel
  // archive (kondo-parity 1.10). Same shape as SALESNAV_ARCHIVE.
  if (first === 'recruiter' && segments[1] === 'archive') {
    const conv =
      segments[2] === CONV_SEGMENT && segments[3]
        ? decodeURIComponent(segments[3])
        : null
    return {
      ...DEFAULT_ROUTE,
      activeFilter: 'RECRUITER_ARCHIVE',
      selectedUrn: conv,
    }
  }

  // /<other-filter> or /<other-filter>/c/:urn
  const filterKey = FILTER_SLUG_TO_KEY[first]
  if (filterKey) {
    const conv =
      segments[1] === CONV_SEGMENT && segments[2]
        ? decodeURIComponent(segments[2])
        : null
    return {
      ...DEFAULT_ROUTE,
      activeFilter: filterKey,
      selectedUrn: conv,
    }
  }

  // Unknown → fall back to default
  return { ...DEFAULT_ROUTE }
}

// ─── Re-exports ──────────────────────────────────────────────────────────────

/** Publicly-useful for direct access to the filter-key → URL slug map. */
export { FILTER_KEY_TO_SLUG, INBOX_VIEW_KEY_TO_SLUG, VALID_SETTINGS_SECTIONS }
